import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useListEventsQuery } from "@/state/eventsApi";
import type { UpcomingEvent } from "@/types/events";

export default function UpcomingEvents() {
  const { t } = useTranslation();
  const { data, isLoading } = useListEventsQuery({ type: 'upcoming' });
  const events = (data?.data ?? []) as UpcomingEvent[];
  const total = data?.meta?.total ?? 0;

  return (
    <section className="py-24 bg-white">
      <div className="w-full px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <div>
            <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-3">{t("upcomingTag")}</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight">
              {t("upcomingTitle1")} <span className="text-[#00C2C2]">{t("upcomingTitle2")}</span>
            </h2>
            <p className="text-gray-500 text-base mt-4 max-w-xl">{t("upcomingDesc")}</p>
          </div>
          <span className="text-sm text-gray-400 font-medium">{total} {t("upcomingCount")}</span>
        </div>

        <div className="flex flex-col gap-6">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse flex flex-col lg:flex-row gap-0 rounded-lg border border-gray-100 overflow-hidden">
                  <div className="w-full lg:w-80 h-52 lg:h-auto bg-gray-100 shrink-0" />
                  <div className="flex-1 p-6 lg:p-8 flex flex-col gap-4">
                    <div className="flex gap-2">
                      <div className="h-5 bg-gray-100 rounded-full w-20" />
                      <div className="h-5 bg-gray-100 rounded-full w-16" />
                    </div>
                    <div className="h-5 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-full" />
                    <div className="h-3 bg-gray-100 rounded w-5/6" />
                    <div className="h-1.5 bg-gray-100 rounded-full w-full mt-auto" />
                  </div>
                </div>
              ))
            : events.map((event, i) => (
                <div
                  key={event.id}
                  className={`flex flex-col lg:flex-row gap-0 rounded-lg border border-gray-100 overflow-hidden hover:border-[#00C2C2]/40 transition-all group ${i % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}
                >
                  <div className="w-full lg:w-80 h-52 lg:h-auto shrink-0 overflow-hidden">
                    {event.image
                      ? <img src={event.image} alt={event.title} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full bg-[#0A1628]/20" />
                    }
                  </div>

                  <div className="flex-1 p-6 lg:p-8 flex flex-col justify-between">
                    <div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0A1628]/10 text-[#00C2C2]">{event.type}</span>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${event.format === "Online" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                          <i className={`${event.format === "Online" ? "ri-global-line" : "ri-map-pin-2-line"} mr-1`} />
                          {event.format === "Online" ? t("upcomingOnline") : t("upcomingInPerson")}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#0A1628] mb-2 group-hover:text-[#00C2C2] transition-colors leading-snug">{event.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">{event.description}</p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-calendar-line text-[#00C2C2]" />
                          {new Date(event.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                          {event.endDate != null && event.endDate !== event.date &&
                            ` – ${new Date(event.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}`
                          }
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-map-pin-2-line text-[#00C2C2]" />
                          {event.location}
                        </span>
                      </div>

                      {event.capacity != null && (
                        <div className="mb-1">
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>{event.registered} {t("detailRegistered")}</span>
                            <span>{event.capacity - event.registered} {t("upcomingSpotsLeft")}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#00C2C2] rounded-full transition-all"
                              style={{ width: `${(event.registered / event.capacity) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 mt-6">
                      <Link
                        to={`/events/${event.id}`}
                        className="bg-[#0A1628] text-white text-sm font-bold px-6 py-2.5 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                      >
                        {t("upcomingViewDetails")} <i className="ri-arrow-right-line" />
                      </Link>
                      <Link
                        to={`/events/${event.id}`}
                        className="bg-[#0A1628]/10 text-[#00C2C2] text-sm font-bold px-6 py-2.5 rounded-lg hover:bg-[#0A1628] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {t("upcomingRegister")}
                      </Link>
                    </div>
                  </div>
                </div>
              ))
          }

          {!isLoading && events.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-calendar-line text-3xl mb-3 block" />
              <p className="font-semibold text-[#0A1628]">No upcoming events</p>
              <p className="text-sm mt-1">Check back soon for new events.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
